/** Console v0.5 Meetings: the list's order and words, and the notes download (built in the browser from the notes the
 * person can already see). Pure -- tested with Node (web/console/test/v05-meetings.test.ts). */

import type { MeetingRow } from "../api.js";

export const MEETINGS_V05_COPY = {
  intro: "Record on your own laptop or phone, online or in person. Preekie writes the notes; you check them.",
  download: "Download notes",
  none: "No meetings yet.",
  noneBody: "Record a meeting, and its notes show here once they're ready.",
  pick: "Choose a meeting to see its notes.",
} as const;

/** Waiting on the person first (to review, being prepared), then upcoming, then approved; newest first within each. */
export function sortMeetings(rows: MeetingRow[]): MeetingRow[] {
  const rank = (r: MeetingRow) => (r.status === "Awaiting review" || r.status === "Exception" || r.processing ? 0 : r.status === "Approved" ? 2 : 1);
  return [...rows].sort((a, b) => rank(a) - rank(b) || b.scheduled_start.localeCompare(a.scheduled_start));
}

/** The list's one-word state (Meetings mock-up: Notes ready, Preparing notes…). */
export function meetingStateLabel(r: Pick<MeetingRow, "status" | "processing">): string {
  if (r.processing) return "Preparing notes";
  switch (r.status) {
    case "Awaiting review":
      return "Notes ready";
    case "Approved":
      return "Approved";
    case "Exception":
      return "Needs a look";
    default:
      return "Not recorded yet";
  }
}

export function matchesMeeting(r: Pick<MeetingRow, "title" | "client_name">, q: string): boolean {
  const s = q.trim().toLowerCase();
  return !s || r.title.toLowerCase().includes(s) || r.client_name.toLowerCase().includes(s);
}

/** The file name the server gave (Content-Disposition), else preekie-notes-<first 8 of the id>.txt (A s.18). */
export function downloadName(disposition: string | null, meetingId: string): string {
  const m = /filename="?([^";]+)"?/.exec(disposition ?? "");
  const name = m?.[1]?.trim() ?? "";
  // Only a plain file name: letters, digits, dot, dash, underscore, not starting with a dot (no paths).
  return /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/.test(name) ? name : `preekie-notes-${meetingId.slice(0, 8)}.txt`;
}

export function renameProblem(title: string): string | null {
  const n = title.trim().length;
  return n >= 1 && n <= 120 ? null : "Enter a name of up to 120 characters.";
}

/** Delete meeting: the approved v0.5 Meetings mock-up's words, exactly (option (a): a small consent record is kept). */
export const DELETE_COPY = {
  title: "Delete this meeting?",
  intro: (what: string) => ["This permanently deletes the audio, transcript and notes for ", what, " for everyone in your account."] as const,
  handedOver: (name: string) => ["You handed these notes over to ", name, ". They will lose access too."] as const,
  kept: "Preekie keeps a small consent record: when consent was given and when the meeting was deleted. It holds no audio, transcript, notes or names.",
  cannotUndo: "You can't undo this.",
  keep: "Keep meeting",
  confirm: "Delete permanently",
  deletedTitle: "This meeting has been deleted.",
  deletedBody: "The audio, transcript and notes are gone for everyone. Preekie keeps only a small consent record.",
} as const;
